import useSWR, { mutate } from 'swr';
import moment from 'moment';
import { connect } from 'react-redux';
import { Translate, translate } from 'react-i18nify';
import Button from '../../../Button';
import { showNotification } from '../../../../actions';
import { DATE_FORMAT } from '../../../../constants';
import api from '../../../../api/apiClient';
import { Program } from '../../ProgramManagement';

const STATUS_CLASS: Record<string, string> = {
  investigation: 'status-pending',
  onboarding: 'status-pending',
  titration: 'status-active',
  treatment: 'status-active',
  reactivation: 'status-pending',
  aborting: 'status-cancelled',
  aborted: 'status-expired'
};

const TreatmentAreas = ({ authToken, showNotification, patient }) => {
  const { data: treatmentAreas = [], isLoading } = useSWR<TreatmentArea[]>(
    patient?.guid ? `/careplan/clinic/${patient.guid}` : null
  );
  const { data: programs = [] } = useSWR<Program[]>(patient?.guid ? `/admin/${patient.guid}/program` : null);
  const reactivationProgramName = 'NurseReactivationProgram';

  const hasOngoingReactivation = programs.some(
    (program) => program.name === reactivationProgramName && program.state.toLowerCase() === 'ongoing'
  );

  const resendReturnEmail = async () => {
    if (!patient?.guid) return;
    try {
      const res = await api.resendReturnMonitorEmail(authToken, patient.guid);
      const json = await res.json();
      if (res.ok) {
        showNotification(json.message, 'success');
      } else {
        showNotification(json.message || translate('error_boundary.header'), 'error');
      }
    } catch {
      showNotification(translate('error_boundary.header'), 'error');
    }
  };

  const confirmStartNurseReactivationProgram = () => {
    if (window.confirm(translate('member_details.treatment_areas.reactivate_care_confirm_header'))) {
      startNurseReactivationProgram();
    }
  };

  const startNurseReactivationProgram = async () => {
    if (!patient?.guid) return;
    try {
      const res = await api.startNurseReactivationProgram(authToken, patient.guid);
      const json = await res.json();
      if (res.ok) {
        showNotification(json.message, 'success');
      } else {
        showNotification(json.message || translate('error_boundary.header'), 'error');
      }
    } catch (e) {
      showNotification(translate('error_boundary.header'), 'error');
    }
    mutate(`/careplan/clinic/${patient.guid}`);
    mutate(`/admin/${patient.guid}/program`);
  };

  const hasAbortedArea = treatmentAreas.some((area) => ['ABORTING', 'ABORTED'].includes(area.treatmentProcess));
  const canReactivate = hasAbortedArea && !hasOngoingReactivation;

  return (
    <div>
      {!isLoading && !treatmentAreas.length ? (
        <span className="text-muted">Inga vårdspår</span>
      ) : (
        <table className="table is-fullwidth">
          <thead>
            <tr>
              <th>Vårdspår</th>
              <th>Status</th>
              <th>Åtgärd</th>
            </tr>
          </thead>
          <tbody>
            {treatmentAreas.map((area, i) => {
              const processKey = area.treatmentProcess.toLowerCase();
              const isAbortingOrAborted = ['ABORTING', 'ABORTED'].includes(area.treatmentProcess);

              return (
                <tr key={i}>
                  <td>
                    <Translate value={`member_details.treatment_areas.area.${area.carePlan}`} />
                  </td>
                  <td className="no-wrap">
                    <span className={`subscription-status-badge ${STATUS_CLASS[processKey] || 'status-pending'}`}>
                      <Translate value={`member_details.treatment_areas.state.${processKey}`} />
                    </span>
                    {isAbortingOrAborted && (
                      <span className="ml-5 text-muted">{moment(area.updated, DATE_FORMAT).format('YYYY-MM-DD')}</span>
                    )}
                  </td>
                  <td>
                    {area.carePlan === 'hypertension' && area.treatmentProcess === 'ABORTING' && (
                      <Button buttonType="text" onClick={resendReturnEmail}>
                        <Translate value="member_details.treatment_areas.resend_return_monitor_email" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {canReactivate && (
        <Button buttonType="text" onClick={confirmStartNurseReactivationProgram}>
          <Translate value="member_details.treatment_areas.reactivate_care" />
        </Button>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    authToken: state.auth.token.jwt,
    patient: state.members.currentMember
  };
};

const mapActionsToProps = {
  showNotification
};

export default connect(mapStateToProps, mapActionsToProps)(TreatmentAreas);
